import { useCallback, useMemo, useState } from 'react';

import { signOut } from 'aws-amplify/auth';
import { BroadcastChannel } from 'broadcast-channel';
import { notifyError } from 'components/common/Toast/Toast';
import { usePhoneCall } from 'contexts/phoneCallContext/phoneCallContext';
import { useHandleEndVideoCall } from 'hooks/videoCall';
import Cookies from 'js-cookie';
import { apiSlice } from 'store/api/apiSlice';
import { resetBulkEdits } from 'store/bulkEdit/bulkEditSlice';
import { closeModal } from 'store/modal/modalSlice';
import { clearAllNotifications } from 'store/notifications/notificationsSlice';
import { clearQaCache } from 'store/qa/qaCacheSlice';
import { clearQaCacheIdb } from 'store/qa/qaCacheStorage';
import { resetQueueState } from 'store/queue/queueSlice';
import { reset } from 'store/slidingInPane/slidingInPaneSlice';
import { setStartLocation } from 'store/system/systemSlice';
import { resetTasks } from 'store/tasks/tasksSlice';
import { clearUser } from 'store/user/userSlice';
import { closeAllSockets } from 'utils/socket';

import { useAppDispatch } from '../redux';

export const useLogout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const handleEndVideoCall = useHandleEndVideoCall();
  const { resetPhoneCallProps } = usePhoneCall();
  const logoutChannel = useMemo(() => new BroadcastChannel('logout'), []);

  const logout = useCallback(async () => {
    setIsLoading(true);

    // Send logout message to all tabs via BroadcastChannel
    if (!logoutChannel.isClosed) logoutChannel.postMessage('Logout');

    try {
      await signOut();
      dispatch(apiSlice.util.resetApiState());
      dispatch(clearUser());
      dispatch(clearAllNotifications());
      dispatch(resetQueueState());
      dispatch(resetBulkEdits());
      closeAllSockets();
      dispatch(closeModal());
      resetPhoneCallProps();
      await handleEndVideoCall();
      dispatch(setStartLocation(null));
      dispatch(resetTasks());
      dispatch(reset());
      dispatch(clearQaCache());
      void clearQaCacheIdb().catch((error) => {
        if (error) {
          notifyError(JSON.stringify(error));
        }
      });
      Cookies.remove('staff_announcement');
      setIsLoading(false);
    } catch (error) {
      if (error) {
        notifyError(JSON.stringify(error));
      }
      setIsLoading(false);
    }
  }, [dispatch, logoutChannel, resetPhoneCallProps, handleEndVideoCall]);

  const logoutAllTabs = () => {
    logoutChannel.onmessage = (broadcastChannelMessage) => {
      if (broadcastChannelMessage === 'Logout') {
        logout();
        logoutChannel.close();
      }
    };
  };

  return { isLoading, logout, logoutAllTabs };
};
